import React, { useState, useEffect, useRef } from "react";
import "react-dates/initialize";
import { DateRangePicker } from "react-dates";
import "react-dates/lib/css/_datepicker.css";
import { CalendarIcon, ArrowDownIcon, IconCross } from "assets/icons.js";
import styled from "styled-components";
import moment from "moment";

// QuickBooks-style quick-select date ranges. Each range() returns
// [startMoment, endMoment]; "Custom dates" leaves the calendar open so the
// user can pick a range manually.
const DATE_PRESETS = [
  { label: "Today", range: () => [moment(), moment()] },
  { label: "This week to date", range: () => [moment().startOf("isoWeek"), moment()] },
  { label: "This month to date", range: () => [moment().startOf("month"), moment()] },
  { label: "This quarter to date", range: () => [moment().startOf("quarter"), moment()] },
  { label: "This year to date", range: () => [moment().startOf("year"), moment()] },
  { label: "Yesterday", range: () => [moment().subtract(1, "day"), moment().subtract(1, "day")] },
  { label: "Last week", range: () => [moment().subtract(1, "week").startOf("isoWeek"), moment().subtract(1, "week").endOf("isoWeek")] },
  { label: "Last month", range: () => [moment().subtract(1, "month").startOf("month"), moment().subtract(1, "month").endOf("month")] },
  { label: "Last quarter", range: () => [moment().subtract(1, "quarter").startOf("quarter"), moment().subtract(1, "quarter").endOf("quarter")] },
  { label: "Last year", range: () => [moment().subtract(1, "year").startOf("year"), moment().subtract(1, "year").endOf("year")] },
  { label: "Next week", range: () => [moment().add(1, "week").startOf("isoWeek"), moment().add(1, "week").endOf("isoWeek")] },
  { label: "Next month", range: () => [moment().add(1, "month").startOf("month"), moment().add(1, "month").endOf("month")] },
  { label: "Next year", range: () => [moment().add(1, "year").startOf("year"), moment().add(1, "year").endOf("year")] },
  { label: "Custom dates", custom: true },
];

const StyledPresetPanel = styled.div`
  width: 210px;
  max-height: 335px;
  overflow-y: auto;
  position: relative;
  border-right: 1px solid #e4e7e7;
  padding: 8px 0;
  font-family: Montserrat Alternates-semiBold;
  box-sizing: border-box;

  /* Always-visible scrollbar so it's clear the list scrolls. */
  scrollbar-width: thin;
  scrollbar-color: #b0b3ba #eef0f2;
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: #eef0f2;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #b0b3ba;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #8e9198;
  }

  .preset-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 16px;
    font-size: 13px;
    color: #484848;
    cursor: pointer;
    white-space: nowrap;
  }
  .preset-item:hover {
    background: #f2f2f2;
  }
  .preset-item.active {
    background: #e8f4ff;
    color: #008489;
    font-weight: 600;
  }
  .preset-check {
    color: #008489;
    font-size: 14px;
    margin-left: 8px;
  }
  @media (max-width: 600px) {
    width: 150px;
    max-height: 260px;
    .preset-item {
      font-size: 12px;
      padding: 8px 12px;
    }
  }
`;

const StyledDateRangePickerUI = styled.div`
  font-family: Montserrat Alternates-semiBold;
  font-size: 15px;
  line-height: 13px;
  width: 100%;
  svg {
    cursor: pointer;
    font-size: 16px;
    vertical-align: top;
  }
  input {
    font-family: Montserrat Alternates-semiBold;
    font-size: 11px;
    line-height: 13px;
    color: #777980;
    vertical-align: top;
    padding: 5px;
    padding-top: 8px;
    padding-left: 10px;
    background-color: #232631;
    @media (max-width: 600px) {
      font-size: 9px;
    }
  }
  .DateRangePickerInput {
    background-color: #232631;
    display: inline-block;
    border: 1px solid #707070;
    height: 30px;
  }
  .DateRangePickerInput__showClearDates {
    padding-right: 0px;
  }
  .DateInput {
    width: 128px;
    vertical-align: top;
    @media (max-width: 600px) {
      width: 100px;
    }
  }
  .DateInput_input__focused {
    border-bottom: 2px solid #e5eced;
  }
  .DateRangePickerInput_arrow_svg {
    vertical-align: top;
    fill: #8e8686;
    height: 24px;
    width: 24px;
  }
  .DateRangePicker_picker__directionLeft {
    left: 50px;
  }
  .CalendarMonth_caption {
    color: #484848;
    font-size: 18px;
    text-align: center;
    padding-top: 32px;
    padding-bottom: 37px;
    caption-side: initial;
  }

  .DateRangePickerInput_arrow {
    display: inline-block;
    vertical-align: top;
    color: #a19191;
  }
  .DateRangePickerInput__showClearDates {
    padding-right: 30px;
  }
  .DateRangePickerInput_calendarIcon {
    background: 0 0;
    border: 0;
    color: inherit;
    font: inherit;
    line-height: normal;
    overflow: visible;
    cursor: pointer;
    padding: 10px;
    margin: 0 10px 0 5px;
    position: absolute;
    top: 50%;
    right: 6%;
    transform: translateY(-50%);
    @media (max-width: 600px) {
      right: 0;
    }
  }
  .DateRangePickerInput_clearDates {
    right: 0%;
    padding: 3px;
    @media (max-width: 600px) {
    left: 100%;}
  }
  .CalendarDay__selected_span {
    background: #232631; //background
    color: white; //text
    border: 1px solid $light-red; //default styles include a border
  }
`;
export default function DateRangePickerUI({
  handleDate,
  childFunc,
  startDate,
  endDate,
  focusedInput,
  setFocusedInput,
  setStartDate,
  setEndDate,
}) {
  // const [startDate, setStartDate] = useState(null);
  // const [endDate, setEndDate] = useState(null);
  // const [focusedInput, setFocusedInput] = useState(null);

  // useEffect(() => {
  //   if (childFunc) {
  //     childFunc.current = handleClear;
  //   }
  // }, []);
  // const handleClear = () => {
  //   setStartDate(null);
  //   setEndDate(null);
  // };
  const [isMobile, setIsMobile] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const listRef = useRef(null);
  const activeItemRef = useRef(null);

useEffect(() => {
  const handleResize = () => {
    setIsMobile(window.innerWidth <= 600);
  };

  handleResize(); // run initially
  window.addEventListener("resize", handleResize);
  return () => window.removeEventListener("resize", handleResize);
}, []);

  // When the calendar opens, bring the currently selected preset to the top
  // of the (scrollable) preset list so it is always in view.
  useEffect(() => {
    if (focusedInput && listRef.current && activeItemRef.current) {
      listRef.current.scrollTop = activeItemRef.current.offsetTop;
    }
  }, [focusedInput, selectedPreset]);

  // Given a picked range, find the preset whose range matches it (compared by
  // day) so a manual calendar selection also ticks the matching left option.
  const matchPreset = (start, end) => {
    if (!start || !end) return "Custom dates";
    const found = DATE_PRESETS.find((preset) => {
      if (preset.custom) return false;
      const [rawStart, rawEnd] = preset.range();
      return start.isSame(rawStart, "day") && end.isSame(rawEnd, "day");
    });
    return found ? found.label : "Custom dates";
  };

  const applyPreset = (preset) => {
    setSelectedPreset(preset.label);
    if (preset.custom) {
      // Keep the calendar open so the user can pick a range manually.
      return;
    }
    const [rawStart, rawEnd] = preset.range();
    const start = rawStart.clone().startOf("day");
    const end = rawEnd.clone().startOf("day");
    setStartDate(start);
    setEndDate(end);
    handleDate(start.toDate(), end.toDate());
    // Close the picker popover after a preset is chosen.
    setFocusedInput(null);
  };

  const renderPresetPanel = () => (
    <StyledPresetPanel ref={listRef}>
      {DATE_PRESETS.map((preset) => {
        const isActive = selectedPreset === preset.label;
        return (
          <div
            key={preset.label}
            ref={isActive ? activeItemRef : null}
            className={`preset-item${isActive ? " active" : ""}`}
            onClick={() => applyPreset(preset)}
          >
            <span>{preset.label}</span>
            {isActive && <span className="preset-check">✓</span>}
          </div>
        );
      })}
    </StyledPresetPanel>
  );

  return (
    <>
      <StyledDateRangePickerUI>
        <DateRangePicker
          startDate={startDate}
          startDateId="s_id"
          endDate={endDate}
          endDateId="e_id"
          onDatesChange={({ startDate, endDate }) => {
            // Tick the matching preset if the picked range equals one,
            // otherwise fall back to "Custom dates".
            setSelectedPreset(matchPreset(startDate, endDate));
            setStartDate(startDate);
            setEndDate(endDate);
            handleDate(moment(startDate).toDate(), moment(endDate).toDate());
          }}
          renderCalendarInfo={renderPresetPanel}
          calendarInfoPosition="before"
          firstDayOfWeek={1}
          anchorDirection="right"
          isOutsideRange={() => false}
          // initialVisibleMonth={() => moment().subtract(2, "M")}
          // isOutsideRange ={ day => day.isAfter(moment()) || day.isBefore(moment().subtract(2, "Y"))}
          focusedInput={focusedInput}
          startDatePlaceholderText="MM/DD/YYYY"
          endDatePlaceholderText="MM/DD/YYYY"
          onFocusChange={(e) => setFocusedInput(e)}
          displayFormat="MM/DD/YYYY"
          showClearDates={true}
          customCloseIcon={<IconCross />}
          customInputIcon={<CalendarIcon />}
          inputIconPosition="after"
          noBorder
            numberOfMonths={isMobile ? 1 : 2}
        />
      </StyledDateRangePickerUI>
    </>
  );
}
